import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from "react";
import * as XLSX from "xlsx";
import SpreadsheetViewer, {
	LoadWorkbook,
	WorkbookCacheEntry,
} from "../components/SpreadsheetViewer";
import { chatApi } from "../services/chatApi";
import { downloadFile } from "../services/fileApi";
import { FileSubkind } from "../types/fileType";
import { ParsedDopplerUri, parseDopplerUri } from "../utils/dopplerUri";

// Bound the parsed-workbook cache; each entry holds a fully parsed grid, so we
// keep only the most-recently-used handful. Provider scope already caps the
// lifetime to the session.
const WORKBOOK_CACHE_MAX = 5;

interface OpenArgs {
	parsed: ParsedDopplerUri;
	chatId?: string | null;
	filename?: string;
}

interface SpreadsheetViewerContextValue {
	/** Open the in-app viewer for a parsed ``doppler://excel`` URI. */
	openSpreadsheet: (args: OpenArgs) => void;
	/** Convenience: parse a raw ``doppler://`` href and open it. Returns
	 * ``false`` if the href is not a viewable Excel URI (caller can fall
	 * back to the default open-in-tab behavior). */
	openSpreadsheetHref: (
		href: string,
		opts?: { chatId?: string | null; filename?: string },
	) => boolean;
	/**
	 * The shared, deduplicated workbook loader, for surfaces that EMBED a
	 * `SpreadsheetView` rather than opening the modal (the KPI drill-in).
	 *
	 * Exposed so there is one cache: a workbook already fetched for the modal —
	 * or for another inline render of the same file — costs nothing to show
	 * again, which is what makes a per-component expand cheap.
	 */
	loadWorkbook: LoadWorkbook;
}

const SpreadsheetViewerContext =
	createContext<SpreadsheetViewerContextValue | null>(null);

export const SpreadsheetViewerProvider: React.FC<{
	children: React.ReactNode;
}> = ({ children }) => {
	const [open, setOpen] = useState<OpenArgs | null>(null);

	// Promise cache keyed by file identity (NOT signed URL — those rotate, and
	// NOT sheet/cell/range — those are view state). Survives modal open/close.
	const cacheRef = useRef<Map<string, WorkbookCacheEntry>>(new Map());

	const loadWorkbook = useCallback<LoadWorkbook>((parsed, chatId) => {
		const key = `${parsed.kind}:${parsed.subkind}:${chatId ?? ""}:${
			parsed.target
		}`;
		const cache = cacheRef.current;
		const existing = cache.get(key);
		if (existing) {
			// Refresh LRU recency.
			cache.delete(key);
			cache.set(key, existing);
			return existing;
		}
		// Server-provided filename, captured during the fetch and stamped onto the
		// cache entry so the viewer can title itself even when the citation that
		// opened it carried no filename (e.g. older extraction artifacts).
		let resolvedName: string | undefined;
		const promise = (async () => {
			// Fetch bytes via the backend proxy (same-origin, already CORS-allowed)
			// rather than a GCS signed URL directly, so per-tenant buckets don't
			// each need browser-origin CORS configured.
			let buf: ArrayBuffer;
			if (parsed.subkind === FileSubkind.ARTIFACT) {
				if (!chatId) throw new Error("Missing chat id for workbook artifact.");
				const blob = await chatApi.downloadWorkbookArtifact(
					chatId,
					parsed.target,
				);
				buf = await blob.arrayBuffer();
			} else {
				const fileId = Number(parsed.target);
				if (!Number.isInteger(fileId) || fileId <= 0) {
					throw new Error("Invalid workbook file id.");
				}
				const dl = await downloadFile(fileId);
				buf = dl.bytes;
				resolvedName = dl.filename ?? undefined;
			}
			return XLSX.read(buf, { type: "array", cellStyles: false });
		})();
		const entry: WorkbookCacheEntry = { promise };
		promise.then(
			(wb) => {
				entry.value = wb;
				entry.filename = resolvedName;
			},
			() => {
				// Evict failed promises so a transient error doesn't poison the
				// file. Identity-guard so a retry that re-took the key isn't dropped.
				if (cache.get(key) === entry) cache.delete(key);
			},
		);
		cache.set(key, entry);
		while (cache.size > WORKBOOK_CACHE_MAX) {
			const oldest = cache.keys().next().value;
			if (oldest === undefined) break;
			cache.delete(oldest);
		}
		return entry;
	}, []);

	const openSpreadsheet = useCallback((args: OpenArgs) => {
		setOpen(args);
	}, []);

	const openSpreadsheetHref = useCallback(
		(href: string, opts?: { chatId?: string | null; filename?: string }) => {
			const parsed = parseDopplerUri(href);
			if (!parsed || parsed.kind !== "excel") return false;
			// Prefer an explicitly-passed filename; otherwise use the one the URI
			// carries (``&file=``) so the viewer header names the workbook.
			setOpen({
				parsed,
				chatId: opts?.chatId,
				filename: opts?.filename ?? parsed.filename,
			});
			return true;
		},
		[],
	);

	const value = useMemo(
		() => ({ openSpreadsheet, openSpreadsheetHref, loadWorkbook }),
		[openSpreadsheet, openSpreadsheetHref, loadWorkbook],
	);

	return (
		<SpreadsheetViewerContext.Provider value={value}>
			{children}
			{open && (
				<SpreadsheetViewer
					parsed={open.parsed}
					chatId={open.chatId}
					filename={open.filename}
					loadWorkbook={loadWorkbook}
					onClose={() => setOpen(null)}
				/>
			)}
		</SpreadsheetViewerContext.Provider>
	);
};

/** Access the viewer opener. Returns ``null`` outside a provider so callers
 * can degrade gracefully to open-in-tab. */
export const useSpreadsheetViewer = (): SpreadsheetViewerContextValue | null =>
	useContext(SpreadsheetViewerContext);
